import React, { useCallback, memo, useState } from 'react'
import { useTranslation } from '../../../../hooks/locale'
import type { GalleryProps } from './types'
import MainImageComponent from './main-image-component'
import ThumbnailImageComponent from './thumbnail-image-component'
import OverlayModal from './overlay-modal'
import FullScreenModal from './full-screen-modal'
import { useCategoryFilteredGallery } from './hooks/use-category-filtered-gallery'

import { GridContainer, ThumbnailsGrid, ViewAllOverlay, GalleryIcon } from './styles'

const GalleryGrid: React.FC<GalleryProps> = ({
    images,
    video,
    skipFullScreen = false,
    skipOverlay = false,
    imageBorder,
    imageBorderRadius,
    onSlideChange,
    onGalleryImageClick,
    onViewAllClick,
    onFullscreenImageClick,
    onFullScreenOpen,
    onOverlayOpen,
    footer,
    header,
    layout,
    fontFamily,
    galleryIconSrc,
    enableCategorizedGallery = false,
}) => {
    const { t } = useTranslation()
    const [isFullScreen, setFullScreen] = useState(false)
    const [isOverlayOpen, setOverlay] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)
    const [shouldAutoplayVideo, setShouldAutoplayVideo] = useState(false)

    const {
        activeCategoryTab,
        setActiveCategoryTab,
        closeFullScreen,
        availableTabs,
        fullScreenImages,
        fullScreenVideo,
        overlayImages,
        overlayVideo,
        getOverlayIndex,
        syncActiveTabFromOverlayIndex,
        getOverlayCounter,
        overlayIndexFromRawIndex,
        handleFullscreenImageClick,
    } = useCategoryFilteredGallery({
        images,
        video,
        enableCategorizedGallery,
        setFullScreen,
        onFullscreenImageClick,
    })

    const openModal = useCallback(() => {
        if (!skipFullScreen) {
            setFullScreen(true)
        } else if (!skipOverlay) {
            setOverlay(true)
        }
    }, [skipFullScreen, skipOverlay])

    const handleImageClick = useCallback(
        (index: number) => {
            onGalleryImageClick?.({
                imageIndex: index,
                layout: layout!,
            })
            setSelectedImageIndex(overlayIndexFromRawIndex(index))
            // Autoplay video only if clicking on index 0 (video position)
            setShouldAutoplayVideo(index === 0)
            openModal()
        },
        [onGalleryImageClick, layout, openModal, overlayIndexFromRawIndex],
    )

    const handleViewAllClick = useCallback(() => {
        onViewAllClick?.()
        setShouldAutoplayVideo(false) // Don't autoplay when viewing all
        setSelectedImageIndex(0)
        setFullScreen(true)
    }, [onViewAllClick])

    const goToSlide = useCallback(
        (index: number) => {
            setSelectedImageIndex(index)
            onSlideChange?.(index)
        },
        [onSlideChange],
    )

    const handleOverlaySlideChange = useCallback(
        (index: number) => {
            goToSlide(index)
            syncActiveTabFromOverlayIndex(index)
        },
        [goToSlide, syncActiveTabFromOverlayIndex],
    )

    if (!images || images.length === 0) {
        return null
    }

    const mainImage = video || images[0]
    const thumbnailImages = video ? images.slice(0, 6) : images.slice(1, 7)

    return (
        <>
            <GridContainer>
                {mainImage && (
                    <MainImageComponent
                        image={mainImage}
                        handleImageClick={handleImageClick}
                        index={0}
                        imageBorder={imageBorder}
                        imageBorderRadius={imageBorderRadius}
                    />
                )}
                <ThumbnailsGrid>
                    {thumbnailImages.map((image, index) => (
                        <ThumbnailImageComponent
                            key={`thumbnail_${index}`}
                            image={image}
                            index={index + 1}
                            handleImageClick={handleImageClick}
                            imageBorder={imageBorder}
                            imageBorderRadius={imageBorderRadius}
                        />
                    ))}
                </ThumbnailsGrid>
                {images.length > 1 && (
                    <ViewAllOverlay onClick={handleViewAllClick} fontFamily={fontFamily}>
                        <GalleryIcon $iconSrc={galleryIconSrc} />
                        {t('gallery.viewAll')}
                    </ViewAllOverlay>
                )}
            </GridContainer>

            {isFullScreen && (
                <FullScreenModal
                    images={fullScreenImages}
                    video={fullScreenVideo}
                    isFullScreenOpen={isFullScreen}
                    isOverlayOpen={isOverlayOpen}
                    setFullScreen={closeFullScreen}
                    header={header}
                    footer={footer}
                    setOverlay={setOverlay}
                    setSelectedImage={goToSlide}
                    currentIndex={selectedImageIndex}
                    onFullscreenImageClick={handleFullscreenImageClick}
                    onFullScreenOpen={onFullScreenOpen}
                    shouldAutoplayVideo={shouldAutoplayVideo}
                    enableCategorizedGallery={enableCategorizedGallery}
                    availableTabs={availableTabs}
                    activeCategoryTab={activeCategoryTab}
                    onCategoryTabChange={setActiveCategoryTab}
                    getOverlayIndex={getOverlayIndex}
                />
            )}

            {isOverlayOpen && (
                <OverlayModal
                    isOverlayOpen={isOverlayOpen}
                    setOverlay={setOverlay}
                    images={overlayImages}
                    video={overlayVideo}
                    initialSlide={selectedImageIndex}
                    setSelectedImage={handleOverlaySlideChange}
                    footer={footer}
                    onOverlayOpen={onOverlayOpen}
                    shouldAutoplayVideo={shouldAutoplayVideo}
                    getOverlayCounter={getOverlayCounter}
                />
            )}
        </>
    )
}

export default memo(GalleryGrid)
